import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AribaFieldSelect } from '~/components/Shared/AribaFieldSelect/AribaFieldSelect';
import { FieldMappingSelect } from '~/components/Shared/FieldMappingSelect/FieldMappingSelect';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Files } from '~/eds/src/components/Files/Files';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { HtmlEntityType } from '~/eds/src/enums/HtmlEntityType';
import { CrudForm } from '~/eds/src/modules/CrudForm/CrudForm';
import { AribaIntegrationType } from '~/enums/AribaIntegrationType';
import { AribaSyncType } from '~/enums/AribaSyncType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { FileMimeType } from '~/enums/FileMimeType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { downloadFile } from '~/utils/files';

import { isInvalidMapping } from '../../util';

function FieldMapping() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { toast } = useToast();
  const stage = ManageSyncPairStageType.FieldMapping;
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const manageSyncPairStage = useSelector(
    slices.integrations.selectors.selectManageSyncPairStage,
  );
  const { syncPair, syncType: syncTypePair } = activeSyncPair;
  const stageProperties = activeSyncPair[stage] ?? {};
  const mappingData = stageProperties['fieldsMapping'] ?? [];
  const csvFieldMapping = stageProperties['csvFieldMapping'] ?? null;

  const syncTypeOptions = [
    {
      value: AribaSyncType.INBOUND,
      label: HtmlEntityType.LeftArrow,
      tooltip: intl.formatMessage({
        id: 'FieldMapping.toEvisortTooltip',
        defaultMessage: 'To Evisort',
        description: 'Tooltip for inbound sync direction',
      }),
    },
    {
      value: AribaSyncType.OUTBOUND,
      label: HtmlEntityType.RightArrow,
      tooltip: intl.formatMessage({
        id: 'FieldMapping.toAribaTooltip',
        defaultMessage: 'To Ariba',
        description: 'Tooltip for outbound sync direction',
      }),
    },
  ];

  if (syncTypePair === AribaIntegrationType.ONE_TIME) {
    // the bi-directional sync is only available for one time sync
    syncTypeOptions.push({
      value: AribaSyncType.BIDIRECTIONAL,
      label: HtmlEntityType.RightLeftArrow,
      tooltip: intl.formatMessage({
        id: 'FieldMapping.bidirectionalTooltip',
        defaultMessage: 'Bi-directional',
        description: 'Tooltip for bidirectional sync direction',
      }),
    });
  }

  const [validateCsv] = api.endpoints.validateFieldMappingCsv.useMutation();
  const {
    data: initialEvisortFieldsResponse,
    isFetching: isFetchingInitialEvisortFields,
  } = api.endpoints.getFields.useQuery({
    limit: 3,
    search: 'Renewal',
  });

  const setMappingData = (mapping) => {
    const areAllFieldsMapped = mapping.every((field) =>
      // if either evisort or ariba is selected, then all fields must be mapped
      // otherwise, the empty row is ignored
      (field.evisort ?? field.ariba ?? field.sync)
        ? field.ariba && field.evisort && field.sync
        : true,
    );
    const updatedValues = {
      ...stageProperties,
      fieldsMapping: mapping,
      areAllFieldsMapped,
    };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [manageSyncPairStage]: updatedValues,
      }),
    );
  };

  useEffect(() => {
    if (initialEvisortFieldsResponse && !mappingData.length) {
      setMappingData(
        initialEvisortFieldsResponse.results.map((evisortField) => ({
          evisort: evisortField.id,
          sync: AribaSyncType.OUTBOUND,
          ariba: null,
          meta: {
            evisortField,
          },
        })),
      );
    }
  }, [initialEvisortFieldsResponse]);

  const usedFields = useMemo(() => {
    return mappingData.reduce(
      (acc, { evisort, ariba, meta }) => {
        acc.evisort[evisort] = meta.evisortField;
        acc.ariba[ariba] = ariba;
        return acc;
      },
      {
        ariba: {},
        evisort: {},
      },
    );
  }, [mappingData]);

  const columns = [
    {
      key: 'evisort',
      input: FieldMappingSelect,
      createInputProps: (d) => {
        return {
          filterOption: (option) => {
            const isUsed = option.value in usedFields.evisort;
            return !isUsed;
          },
          isClearable: false,
          isSearchable: false,
          aribaFieldType: getAribaFieldType(d.meta),
        };
      },
      label: intl.formatMessage({
        id: 'FieldMapping.evisortFieldsLabel',
        defaultMessage: 'Evisort Fields',
        description: 'Label for Evisort fields column',
      }),
      mapValue: (field) => field.id,
      name: 'evisort-fields',
      placeholder: intl.formatMessage({
        id: 'FieldMapping.evisortFieldsPlaceholder',
        defaultMessage: 'Select Evisort fields',
        description: 'Placeholder for Evisort fields selection',
      }),
    },
    {
      key: 'sync',
      label: intl.formatMessage({
        id: 'FieldMapping.syncLabel',
        defaultMessage: 'Sync',
        description: 'Label for sync direction column',
      }),
      info: intl.formatMessage({
        id: 'FieldMapping.syncInfo',
        defaultMessage:
          'Sync data seamlessly in field mapping by choosing one-way or bi-directional.',
        description: 'Info text explaining sync direction options',
      }),
      input: Select,
      inputProps: {
        isClearable: false,
        isSearchable: false,
        options: syncTypeOptions,
        width: 'input.s.width',
      },
      name: 'sync',
      placeholder: intl.formatMessage({
        id: 'FieldMapping.syncPlaceholder',
        defaultMessage: 'Select sync mode',
        description: 'Placeholder for sync mode selection',
      }),
    },
    {
      key: 'ariba',
      label: intl.formatMessage({
        id: 'FieldMapping.aribaFieldsLabel',
        defaultMessage: 'Ariba Fields',
        description: 'Label for Ariba fields column',
      }),
      input: AribaFieldSelect,
      createInputProps: (d) => {
        return {
          isClearable: false,
          filterOption: (option) => {
            const isUsed = option.value in usedFields.ariba;
            return !isUsed;
          },
          noOptionsMessage: intl.formatMessage({
            id: 'FieldMapping.aribaFieldsNoOptions',
            defaultMessage:
              'No fields available to map to selected evisort field',
            description:
              'Message when no Ariba fields are available for mapping',
          }),
          evisortFieldType: getEvisortFieldType(d.meta),
          syncpair: syncPair,
        };
      },
      name: 'ariba-fields',
      placeholder: intl.formatMessage({
        id: 'FieldMapping.aribaFieldsPlaceholder',
        defaultMessage: 'Select Ariba fields',
        description: 'Placeholder for Ariba fields selection',
      }),
      mapValue: (field) => field.fieldId,
    },
  ];

  const getMetadataValues = (field, prevMeta) => {
    const newMeta = { ...prevMeta };
    if (field && typeof field === 'object') {
      if (field.hasOwnProperty('fieldId')) {
        newMeta.aribaField = field;
      } else {
        newMeta.evisortField = field;
      }
    }
    return newMeta;
  };

  const handleUpdateMappingData = (updatedMappingData, actionMeta) => {
    const { action, value, datum, index } = actionMeta;
    switch (action) {
      case 'update':
        // if it's an evisort field update, check if the ariba field is compatible
        if (value.hasOwnProperty('id') && datum.ariba) {
          // validate that the mapping is valid
          const evisortFieldType = value.type;
          const aribaFieldType = getAribaFieldType(datum.meta);
          if (isInvalidMapping(evisortFieldType, aribaFieldType)) {
            updatedMappingData[index].meta = {
              ...getMetadataValues(value, updatedMappingData[index].meta),
              aribaField: null,
            };
            updatedMappingData[index].ariba = null;
          }
        } else {
          updatedMappingData[index].meta = {
            ...getMetadataValues(value, updatedMappingData[index].meta),
          };
        }
        break;
      case 'create':
        updatedMappingData[index].meta = {
          ...getMetadataValues(value, updatedMappingData[index].meta),
        };
        break;
      default:
        break;
    }
    setMappingData(updatedMappingData);
  };

  const getEvisortFieldType = (field) => {
    return field.evisortField ? field.evisortField.type : null;
  };

  const getAribaFieldType = (field) => {
    return field.aribaField ? field.aribaField.fieldDataSubType : null;
  };

  const handleSelectedFile = (files) => {
    const file = files[0];
    validateCsv({
      file,
      syncPair,
    })
      .unwrap()
      .then((response) => {
        const fieldMappingFilePath = response.success;

        dispatch(
          slices.integrations.actions.setActiveSyncPair({
            ...activeSyncPair,
            [manageSyncPairStage]: {
              ...stageProperties,
              fieldMappingFilePath,
              csvFieldMapping: file,
            },
          }),
        );

        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'FieldMapping.fileProcessedSuccess',
            defaultMessage: 'File processed successfully',
            description: 'Success message when file is processed',
          }),
        });
      })
      .catch((err) => {
        const message = err.response.data?.error;
        toast({
          status: 'danger',
          message: intl.formatMessage(
            {
              id: 'FieldMapping.fileProcessedError',
              defaultMessage: 'Error processing file: {message}',
              description:
                'Error message when file processing fails. Example: message = "Invalid CSV format"',
            },
            { message },
          ),
        });
      });
  };
  const setCSVFieldMapping = (file) => {
    const updatedValues = {
      ...stageProperties,
      csvFieldMapping: file,
    };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [manageSyncPairStage]: updatedValues,
      }),
    );
  };
  const downloadCsvTemplate = (e) => {
    // create a csv template
    const csvTemplate =
      'Ariba Field,Ariba Label,Ariba Unique Value,Evisort Field,Evisort Value\n';
    const csvBlob = new Blob([csvTemplate], { type: 'text/csv' });
    const filename = 'field_mapping_template.csv';
    downloadFile(csvBlob, filename);
    e.preventDefault();
  };

  const fieldMappingForm = (
    <CrudForm
      actions={{
        add: {
          text: intl.formatMessage({
            id: 'FieldMapping.addFieldButton',
            defaultMessage: 'Add another field',
            description: 'Button text for adding another field',
          }),
          tooltip: intl.formatMessage({
            id: 'FieldMapping.addFieldTooltip',
            defaultMessage: 'Tooltip for add action',
            description: 'Tooltip for add field action',
          }),
        },
        delete: (d) => ({
          disabled: d.sync === 'inbound',
          text: intl.formatMessage({
            id: 'FieldMapping.deleteButton',
            defaultMessage: 'Delete',
            description: 'Button text for deleting field mapping',
          }),
          tooltip:
            d.sync === 'inbound'
              ? intl.formatMessage({
                  id: 'FieldMapping.deleteDisabledTooltip',
                  defaultMessage:
                    'Cannot delete inbound sync, please update the sync type to delete',
                  description:
                    'Tooltip when delete is disabled for inbound sync',
                })
              : intl.formatMessage(
                  {
                    id: 'FieldMapping.deleteConfirmTooltip',
                    defaultMessage:
                      'Delete {evisort} {sync} {ariba} integration?',
                    description:
                      'Confirmation tooltip for deleting field mapping. Example: evisort = "example", sync = "sync-pair", ariba = "example"',
                  },
                  { evisort: d.evisort, sync: d.sync, ariba: d.ariba },
                ),
        }),
      }}
      columns={columns}
      columnWidths={['1fr', 'auto', '1fr']}
      data={mappingData}
      loadingContent={{
        isLoading: isFetchingInitialEvisortFields,
        message: intl.formatMessage({
          id: 'FieldMapping.loadingMessage',
          defaultMessage: 'Retrieving field information',
          description: 'Loading message when retrieving field information',
        }),
      }}
      onUpdate={handleUpdateMappingData}
    />
  );
  return (
    <Layout preset="sections" direction="column">
      <Layout spacing={4} pb={4} direction="column">
        <FormField
          label={intl.formatMessage({
            id: 'FieldMapping.uploadTransformationLabel',
            defaultMessage: 'Upload transformation data',
            description: 'Label for uploading transformation data',
          })}
          input={FileInput}
          value={stageProperties.csvFieldMapping}
          onChange={handleSelectedFile}
          inputProps={{
            accept: {
              [FileMimeType.Csv]: [FileExtensionType.Csv],
            },
            layout: 'condensed',
            enableDropzone: false,
          }}
          description={[
            <FormattedMessage
              id="FieldMapping.uploadTransformationDescription"
              defaultMessage="Upload a CSV file that maps Evisort field data to Ariba field data. Download a <link>CSV template</link> to get started."
              description="Description for uploading transformation data with CSV template link"
              values={{
                link: (chunks) => (
                  <a href="#/" onClick={downloadCsvTemplate}>
                    {chunks}
                  </a>
                ),
              }}
            />,
          ]}
        />

        {csvFieldMapping && (
          <Files
            title={intl.formatMessage({
              id: 'FieldMapping.uploadedFileTitle',
              defaultMessage: 'Uploaded file',
              description: 'Title for uploaded file section',
            })}
            files={[csvFieldMapping]}
            onRemoveFile={() => setCSVFieldMapping(null)}
          />
        )}
      </Layout>
      <FormField
        label={intl.formatMessage({
          id: 'FieldMapping.mapFieldsLabel',
          defaultMessage: 'Map your fields',
          description: 'Label for field mapping section',
        })}
        children={fieldMappingForm}
        description={intl.formatMessage({
          id: 'FieldMapping.mapFieldsDescription',
          defaultMessage:
            'Select your Evisort field type and map it to the corresponding Ariba field type.',
          description: 'Description for field mapping functionality',
        })}
      />
    </Layout>
  );
}

export default FieldMapping;
